import AppBaseStore from './baseStore'
import { useDjango } from '@/composables/useDjango'
import type { BaseStoreActions, BaseStoreType } from '@/types/storeTypes'
import type { SectionSelectionPolygon } from '@/stores/type/selectionPolygon.type'
import API from '@/api/apiUrls'
import { haveMatchingIDs } from '@/utils/arrayUtils'
import type { MultiPolygon, Polygon } from 'geojson'

const { api } = useDjango()

type State = BaseStoreType<SectionSelectionPolygon>

const baseUrl = API.SECTION_GEO_DATA.SELECTION_POLYGONS.INDEX
const baseStore: BaseStoreType<SectionSelectionPolygon> = new AppBaseStore(baseUrl, api)
const baseStoreActions: BaseStoreActions<SectionSelectionPolygon> = baseStore.actions

export const useSelectionPolygonsStores = defineStore({
  id: 'selectionPolygons',
  state: (): State => ({
    ...baseStore.state,
    filterFields: ['name', 'zone_name'],
    updatePolygonDialog: false,
    newPolygonCoordinates: null,
    addPolygonShapeFileDialog: false,
    selectedItems: []
  }),
  actions: {
    ...baseStoreActions,
    resetAll() {
      this.$reset()
    },
    async removeSelectedPolygons() {
      try {
        this.selectedItems = this.selectedItems.map((item) => {
          return {
            ...item,
            sectionline: item.properties.section_line
          }
        })
        await this.bulkDelete(this.selectedItems)
        this.selectedItems = []
      } catch (error) {
        console.error('Failed to remove polygons:', error)
      }
    },
    toggleUpdatePolygonDialog(item: SectionSelectionPolygon | null = null) {
      if (item) {
        this.setCurrentItem(item)
      }
      this.updatePolygonDialog = !this.updatePolygonDialog
    },
    setPolygonCoordinates(coordinates: Polygon | MultiPolygon) {
      this.newPolygonCoordinates = coordinates
    },
    toggleSelectedPolygons(polygons: SectionSelectionPolygon[]) {
      if (haveMatchingIDs<SectionSelectionPolygon>(this.selectedItems, polygons)) {
        this.setSelectedItems([])
      } else {
        this.setSelectedItems(polygons)
      }
    },
    addOrRemoveSelectedPolygon(polygon: SectionSelectionPolygon) {
      if (this.selectedItems.map((item) => item.id).includes(polygon.id)) {
        this.selectedItems.splice(this.selectedItems.indexOf(polygon), 1)
      } else {
        this.addSelectedPolygon(polygon)
      }
    },
    addSelectedPolygon(polygon: SectionSelectionPolygon) {
      if (!this.selectedItems.map((item) => item.id).includes(polygon.id)) {
        this.selectedItems.push(polygon)
      }
    },
    toggleAddPolygonShapeFileDialog() {
      this.addPolygonShapeFileDialog = !this.addPolygonShapeFileDialog
    },
    removePolygonsBySectionLineID(sectionLineID: number) {
      this.items = this.items.filter(
        (item: SectionSelectionPolygon) => item.properties.section_line !== sectionLineID
      )
    }
  },
  getters: {
    ...baseStore.getters,
    polygonsBySectionLineId: (state: State) => (sectionLineID: number) => {
      return state.items.filter(
        (item: SectionSelectionPolygon) => item.properties.section_line === sectionLineID
      )
    }
  }
})
